import { Box, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { SearchField } from 'components/common/SearchField'
import {
  filterClusters,
  ShareableListType
} from 'components/configurations/clusters/clusters-utils'
import { OwnedClusterList } from 'components/configurations/clusters/OwnedClusterList'
import { ShareableClusterList } from 'components/configurations/clusters/ShareableClusterList'
import { SEARCH_INPUT_FIELD_WIDTH } from 'components/configurations/configurations-utils'
import {
  SEARCH_PARAMS_KEY,
  useDebouncedSearchParams
} from 'hooks/use-debounced-search-params'

import type {
  AvailableK8sClusterAsset,
  SharedAssetsResponse
} from 'types/api/sharedAssets/sharedAssets'

export const ClustersListContainer: React.FC<{
  sharedAssets: SharedAssetsResponse
}> = ({ sharedAssets }) => {
  const [searchParams] = useSearchParams()
  const [searchValue, setSearchValue] = useDebouncedSearchParams()

  const searchParamsValue = searchParams.get(SEARCH_PARAMS_KEY) ?? ''

  const { myClusters, availableClusters } = sharedAssets

  const { sharedClusters, unsharedClusters } = useMemo(() => {
    return availableClusters.reduce(
      (
        acc: {
          sharedClusters: AvailableK8sClusterAsset[]
          unsharedClusters: AvailableK8sClusterAsset[]
        },
        cluster
      ) => {
        if (cluster.currentlySharedNamespaces.length > 0) {
          acc.sharedClusters.push(cluster)
        } else {
          acc.unsharedClusters.push(cluster)
        }

        return acc
      },
      { sharedClusters: [], unsharedClusters: [] }
    )
  }, [availableClusters])

  /**
   * Search Filtering
   */
  const filteredOwnedClusters = useMemo(
    () => filterClusters(myClusters, searchParamsValue),
    [searchParamsValue, myClusters]
  )

  const filteredSharedClusters = useMemo(
    () => filterClusters(sharedClusters, searchParamsValue),
    [searchParamsValue, sharedClusters]
  )

  const filteredUnsharedClusters = useMemo(
    () => filterClusters(unsharedClusters, searchParamsValue),
    [searchParamsValue, unsharedClusters]
  )
  /**
   * End Search Filtering
   */

  return (
    <Stack data-testid="clusters-list-container" sx={{ rowGap: 4 }}>
      <SearchField
        data-testid="clusters-list-search"
        helperText="Search by cluster name or namespace"
        InputProps={{ sx: { width: SEARCH_INPUT_FIELD_WIDTH } }}
        label="Search"
        value={searchValue}
        onChange={setSearchValue}
      />
      <Stack sx={{ rowGap: 2 }}>
        <Typography sx={{ fontWeight: 'bold' }} variant="body1">
          Owned
        </Typography>
        <OwnedClusterList
          isFiltered={Boolean(searchValue)}
          ownedClusters={filteredOwnedClusters}
        />
      </Stack>
      <Stack sx={{ rowGap: 2 }}>
        <Typography sx={{ fontWeight: 'bold' }} variant="body1">
          Shared
        </Typography>
        <Box data-testid="shared-cluster-list">
          <ShareableClusterList
            availableClusters={filteredSharedClusters}
            isFiltered={Boolean(searchValue)}
            shareableListType={ShareableListType.SHARED}
          />
        </Box>
      </Stack>
      <Stack sx={{ rowGap: 2 }}>
        <Typography sx={{ fontWeight: 'bold' }} variant="body1">
          Not Shared
        </Typography>
        <Box data-testid="not-shared-cluster-list">
          <ShareableClusterList
            availableClusters={filteredUnsharedClusters}
            isFiltered={Boolean(searchValue)}
            shareableListType={ShareableListType.NOT_SHARED}
          />
        </Box>
      </Stack>
    </Stack>
  )
}
